import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import { parseError } from '@/hooks/useTeams';

import type {
  CutoverActionsPage,
  CutoverRequestOutcome,
  CutoverStatus,
  CutoverStepLabel,
  ReverseTarget,
} from './cutover.types';

export const AUDIT_LOG_PAGE_SIZE = 25;
// The service's limit on a reason (cutover.contract.ts `reasonText`)
export const REASON_MAX_LENGTH = 1000;
const QUERY_KEY = ['cutover'] as const;
// How often the page rereads the cutover while a move runs
const RUNNING_POLL_MS = 5_000;

// A ramp or reverse is either queued (202) or refused (409); the body has the
// same shape either way, and a refusal is an answer to show, not an error
export type CutoverRequestResult = CutoverRequestOutcome & {
  accepted: boolean;
};

function useCutoverFetch() {
  const { getAccessTokenSilently } = useAuth0();
  return async (path: string, init?: RequestInit): Promise<Response> => {
    const response = await apiFetch(`${API_URL}/admin/cutover${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${await getAccessTokenSilently()}`,
        'Content-Type': 'application/json',
      },
    });
    const isRefusal = response.status === 409;
    if (!response.ok && !isRefusal) throw new Error(await parseError(response));
    return response;
  };
}

export function useCutoverStatusQuery() {
  const cutoverFetch = useCutoverFetch();
  return useQuery({
    queryKey: [...QUERY_KEY, 'status'],
    queryFn: async () =>
      (await cutoverFetch('')).json() as Promise<CutoverStatus>,
    refetchInterval: (query) =>
      query.state.data?.active_action ? RUNNING_POLL_MS : false,
  });
}

export function useCutoverActionsQuery(
  page: number,
  isMoveRunning: boolean,
  // The status's view of the latest actions; a new value rereads the log
  version: string
) {
  const cutoverFetch = useCutoverFetch();
  const params = new URLSearchParams({
    page: String(page),
    limit: String(AUDIT_LOG_PAGE_SIZE),
  });
  return useQuery({
    queryKey: [...QUERY_KEY, 'actions', page, version],
    queryFn: async () =>
      (
        await cutoverFetch(`/actions?${params}`)
      ).json() as Promise<CutoverActionsPage>,
    placeholderData: (previous) => previous,
    refetchInterval: isMoveRunning ? RUNNING_POLL_MS : false,
  });
}

function useCutoverRequestMutation<TVars>(
  path: string,
  toBody: (vars: TVars) => unknown
) {
  const cutoverFetch = useCutoverFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: TVars): Promise<CutoverRequestResult> => {
      const response = await cutoverFetch(path, {
        method: 'POST',
        body: JSON.stringify(toBody(vars)),
      });
      const outcome = (await response.json()) as CutoverRequestOutcome;
      return { ...outcome, accepted: response.status === 202 };
    },
    // Refusals are in the audit log too
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export const useRampCutoverMutation = () =>
  useCutoverRequestMutation(
    '/ramp',
    (vars: { to: CutoverStepLabel; overrideReason: string | null }) => ({
      to: vars.to,
      override_reason: vars.overrideReason ?? undefined,
    })
  );

export const useReverseCutoverMutation = () =>
  useCutoverRequestMutation(
    '/reverse',
    (vars: { target: ReverseTarget; reason: string }) => vars
  );
